import type { ReactElement } from 'react';
import { memo, useEffect, useReducer } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import {
  enableAccessCheckFilterOnGetTeams,
  isApprovalByTeamEnabled,
  memberFilterPageSize,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import type { List, Set } from '@gonfalon/immutable-shim/immutableShim';
import { OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import {
  environment as environmentResourceSpecifier,
  flag as flagResourceSpecifier,
  project as projectResourceSpecifier,
  toString as resourceSpecifierToString,
  segment as segmentResourceSpecifier,
} from '@gonfalon/resource-specifiers/ast';
import { useSegment } from '@gonfalon/rest-api/hooks/useSegment';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useParam } from '@gonfalon/router/useParam';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useQuery } from '@tanstack/react-query';
import { noop } from 'lodash';
import nullthrows from 'nullthrows';

import { useSelector } from '#hooks/useSelector';
import { profileSelector } from '#reducers/profile';
import { getMember } from '#sources/AccountAPI';
import { getTeam, getTeams } from '#sources/TeamsAPI';
import type { ApprovalRequestResourceKind } from '#utils/approvals/types';
import type { Member } from '#utils/members/records/Member';
import { searchMembersWithAccess } from '#utils/members/searchMembers';
import { createTeamsFiltersFromQuery } from '#utils/teams/createTeamsFiltersFromQuery';
import type { Team } from '#utils/teams/records/Team';

import { isReviewerType } from './isReviewerType';
import { isTeamType } from './isTeamType';
import { SelectReviewers } from './SelectReviewers';

const actions = {
  loadSelectedMembers: () =>
    ({
      type: 'loadSelectedMembers',
    }) as const,
  loadSelectedTeams: () =>
    ({
      type: 'loadSelectedTeams',
    }) as const,
  saveSelectedMembers: (members: Member[]) =>
    ({
      type: 'saveSelectedMembers',
      members,
    }) as const,
  saveSelectedTeams: (teams: Team[]) =>
    ({
      type: 'saveSelectedTeams',
      teams,
    }) as const,
  search: (searchValue: string) =>
    ({
      type: 'search',
      searchValue,
    }) as const,
  fetchReviewers: () =>
    ({
      type: 'fetchReviewers',
    }) as const,
  fetchMembers: () =>
    ({
      type: 'fetchMembers',
    }) as const,
  fetchTeams: () =>
    ({
      type: 'fetchTeams',
    }) as const,
  fetchReviewersDone: (reviewers: OrderedMap<string, Member>) =>
    ({
      type: 'fetchReviewersDone',
      reviewers,
    }) as const,
  fetchMembersDone: (members: OrderedMap<string, Member>) =>
    ({
      type: 'fetchMembersDone',
      members,
    }) as const,
  fetchTeamsDone: (teams: OrderedMap<string, Team>) =>
    ({
      type: 'fetchTeamsDone',
      teams,
    }) as const,
  fetchReviewersFailed: () =>
    ({
      type: 'fetchReviewersFailed',
    }) as const,
  fetchMembersFailed: () =>
    ({
      type: 'fetchMembersFailed',
    }) as const,
  fetchTeamsFailed: () =>
    ({
      type: 'fetchTeamsFailed',
    }) as const,
};

function reducer(
  state: {
    isSearchingReviewers: boolean;
    isSearchingMembers: boolean;
    isSearchingTeams: boolean;
    searchValue: string;
    reviewers: OrderedMap<string, Member>;
    members: OrderedMap<string, Member>;
    teams: OrderedMap<string, Team>;
    selectedMembers: Map<string, Member>;
    selectedTeams: Map<string, Team>;
  },
  action: ReturnType<(typeof actions)[keyof typeof actions]>,
) {
  switch (action.type) {
    case 'search':
      return {
        isSearchingReviewers: false,
        isSearchingMembers: false,
        isSearchingTeams: false,
        searchValue: action.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchReviewers':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: true,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchMembers':
      return {
        isSearchingMembers: true,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchTeams':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: true,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchReviewersDone':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: false,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: action.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchMembersDone':
      return {
        isSearchingMembers: false,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: action.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchTeamsDone':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: false,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: action.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchReviewersFailed':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: false,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchMembersFailed':
      return {
        isSearchingMembers: false,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'fetchTeamsFailed':
      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: false,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: state.selectedTeams,
      };
    case 'saveSelectedMembers':
      const updatedCache = state.selectedMembers;

      for (const member of action.members) {
        updatedCache.set(member._id, member);
      }

      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: updatedCache,
        selectedTeams: state.selectedTeams,
      };
    case 'saveSelectedTeams':
      const updatedTeamCache = state.selectedTeams;

      for (const team of action.teams) {
        updatedTeamCache.set(team.key, team);
      }

      return {
        isSearchingMembers: state.isSearchingMembers,
        isSearchingReviewers: state.isSearchingReviewers,
        isSearchingTeams: state.isSearchingTeams,
        searchValue: state.searchValue,
        reviewers: state.reviewers,
        members: state.members,
        teams: state.teams,
        selectedMembers: state.selectedMembers,
        selectedTeams: updatedTeamCache,
      };
    default:
      return state;
  }
}

type SelectReviewerContainerProps = {
  className?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  resourceKind?: ApprovalRequestResourceKind;
  resourceKey?: string;
  value?: Array<Member | Team> | string | null;
  onChange(member?: Member | null | Member[], team?: Team | null | Team[]): void;
  filterPendingInvites?: boolean;
  isClearable?: boolean;
  valueContainerStyles?: object;
  onBlur?(): void;
  closeMenuOnSelect?: boolean;
  filterOutCurrentMember?: boolean;
  filterMembers?: (m: Member) => boolean;
  // filter members from the list of members available to select by their ID
  filterByMemberId?: Set<string>;
  disabled?: boolean;
  profile?: Member;
  isTeamsMemberContainer?: boolean;
  customMultiValueRemoveTooltip?: string;
  customNoOptionsMessage?: ReactElement | string;
  ariaLabel?: string;
  showGroupOptions?: boolean;
  useAccessCheck?: boolean;
  canClearOriginalValues?: boolean;
  isMulti?: boolean;
  defaultMenuIsOpen?: boolean;
  existingReviewers?: List<string>;
};

export const useRedux = () => {
  const profile = useSelector((state) => profileSelector(state));
  return {
    profile: profile.get('entity'),
  };
};

export const useResourceSpec = ({
  enabled,
  resourceKind,
  resourceKey,
}: {
  enabled: boolean;
  resourceKind?: ApprovalRequestResourceKind;
  resourceKey?: string;
}) => {
  const projKey = useProjectKey();
  const envKey = useSelectedEnvironmentKey();
  const flagKey = useParam('flagKey', { optional: true });

  let flagKeyToUse = flagKey;

  const resourceIsFlag = resourceKind === 'Flag' || resourceKind === 'FlagConfiguration';
  if (resourceIsFlag && !flagKey && resourceKey) {
    flagKeyToUse = resourceKey;
  }

  const resourceIsSegment = resourceKind === 'Segment';

  const { data: flag } = useQuery({
    ...getFeatureFlagQuery({ projectKey: projKey, featureFlagKey: flagKeyToUse ?? '' }),
    enabled: enabled && resourceIsFlag,
  });

  const { data: segment } = useSegment(
    { projectKey: projKey, environmentKey: envKey, segmentKey: resourceKey ?? '' },
    { enabled: enabled && resourceIsSegment },
  );

  if (enabled) {
    const project = projectResourceSpecifier(projKey);
    const environment = environmentResourceSpecifier(project, envKey);
    if (resourceIsFlag) {
      return resourceSpecifierToString(flagResourceSpecifier(environment, nullthrows(flagKeyToUse), flag?.tags ?? []));
    }
    if (resourceIsSegment) {
      return resourceSpecifierToString(
        segmentResourceSpecifier(environment, nullthrows(resourceKey), segment?.tags ?? []),
      );
    }
  }
};

// oxlint-disable-next-line prefer-arrow-callback
export const SelectReviewersContainer = memo(function SelectReviewersContainer({
  value,
  disabled,
  closeMenuOnSelect,
  onChange,
  useAccessCheck = true,
  canClearOriginalValues = true,
  isMulti = true,
  defaultMenuIsOpen = false,
  ...props
}: SelectReviewerContainerProps) {
  const { profile } = useRedux();
  const resourceSpec = useResourceSpec({
    enabled: useAccessCheck,
    resourceKind: props.resourceKind,
    resourceKey: props.resourceKey,
  });
  const memberAccessCheck = resourceSpec
    ? (`reviewApprovalRequest:${encodeURIComponent(resourceSpec)}` as const)
    : undefined;
  const accessCheckFilterOnGetTeamsEnabled = enableAccessCheckFilterOnGetTeams();

  const [state, dispatch] = useReducer(reducer, {
    isSearchingMembers: false,
    isSearchingReviewers: false,
    isSearchingTeams: false,
    searchValue: '',
    reviewers: OrderedMap<string, Member>(),
    members: OrderedMap<string, Member>(),
    teams: OrderedMap<string, Team>(),
    selectedMembers: new Map<string, Member>(),
    selectedTeams: new Map<string, Team>(),
  });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const unlessAborted = (action: ReturnType<(typeof actions)[keyof typeof actions]>) => {
      if (!signal.aborted) {
        dispatch(action);
      }
    };

    dispatch(actions.search(state.searchValue));

    dispatch(actions.fetchMembers());
    dispatch(actions.fetchReviewers());
    const { members, membersWithAccess } = searchMembersWithAccess(
      { query: state.searchValue, pageSize: memberFilterPageSize(), accessCheck: memberAccessCheck },
      { signal },
    );
    members
      .then((page) => unlessAborted(actions.fetchMembersDone(page)))
      .catch(() => {
        unlessAborted(actions.fetchMembersFailed());
      });
    membersWithAccess
      .then((reviewers) =>
        unlessAborted(actions.fetchReviewersDone(reviewers.map((member) => member.set('canReview', true)))),
      )
      .catch(() => {
        unlessAborted(actions.fetchReviewersFailed());
      });

    // fetch teams
    if (isApprovalByTeamEnabled()) {
      let teamFilters = createTeamsFiltersFromQuery({
        query: state.searchValue,
        noAccess: false,
      });
      if (accessCheckFilterOnGetTeamsEnabled && resourceSpec) {
        teamFilters = teamFilters
          .set('accessCheck', 'reviewApprovalRequest')
          .set('accessCheckResource', encodeURIComponent(resourceSpec));
      }

      dispatch(actions.fetchTeams());
      getTeams(teamFilters, ['members'])
        .then((data) => dispatch(actions.fetchTeamsDone(data.get('entities').get('teams'))), noop)
        .catch(() => {
          dispatch(actions.fetchTeamsFailed());
        });
    }

    return () => controller.abort();
  }, [state.searchValue]);

  useEffect(() => {
    if (!value) {
      return;
    }

    const ids = Array.isArray(value)
      ? value.map((memberOrTeam) => {
          if (isTeamType(memberOrTeam)) {
            return memberOrTeam.key;
          } else {
            return memberOrTeam._id;
          }
        })
      : [value];

    for (const id of ids) {
      if (state.selectedMembers.has(id) || state.selectedTeams.has(id)) {
        continue;
      }

      getMember(id).then((data) => dispatch(actions.saveSelectedMembers([data])), noop);

      getTeam(id).then((data) => dispatch(actions.saveSelectedTeams([data])), noop);
    }
  }, [value]);

  let finalValue: Member | Team | Array<Member | Team> | null = null;

  if (typeof value === 'string') {
    finalValue =
      state.members.get(value) ||
      state.selectedMembers.get(value) ||
      state.teams.get(value) ||
      state.selectedTeams.get(value) ||
      null; // null is for react-select…
  }

  if (Array.isArray(value) && value.every(isReviewerType)) {
    finalValue = value;
  }

  function handleChange(nextValue: string | string[] | null) {
    if (nextValue === null) {
      handleChange(null);
      return;
    }

    const nextValueIds = Array.isArray(nextValue) ? nextValue : [nextValue];
    const members: Member[] = [];
    const teams: Team[] = [];

    for (const id of nextValueIds) {
      const member =
        state.reviewers.get(id) ||
        state.members.get(id) ||
        state.selectedMembers.get(id) ||
        (profile._id === id && profile);
      const team = state.teams.get(id) || state.selectedTeams.get(id);
      if (member) {
        members.push(member);
      } else if (team) {
        teams.push(team);
      }
    }

    // cache the selected member(s) if we already have them
    dispatch(actions.saveSelectedMembers(members));
    dispatch(actions.saveSelectedTeams(teams));
    // update the parent (which will update the URL, etc…)
    onChange(Array.isArray(nextValue) ? members : members[0], Array.isArray(nextValue) ? teams : teams[0]);
  }

  return (
    <SelectReviewers
      {...props}
      onChange={handleChange}
      onInputChange={(searchValue) => dispatch(actions.search(searchValue))}
      isLoading={state.isSearchingMembers || state.isSearchingReviewers || state.isSearchingTeams}
      disabled={disabled}
      profile={profile}
      reviewers={state.reviewers}
      teams={state.teams}
      members={state.members}
      value={finalValue}
      closeMenuOnSelect={closeMenuOnSelect}
      accessCheck={memberAccessCheck}
      canClearOriginalValues={canClearOriginalValues}
      isMulti={isMulti}
      defaultMenuIsOpen={defaultMenuIsOpen}
    />
  );
});
